import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api, fileUrl, formatFollowers } from "@/lib/api";
import { CompBadge } from "@/components/OpportunityCard";
import { CreatorBadges } from "@/components/Badges";
import { AddToCalendar } from "@/components/AddToCalendar";
import { MatchBadge } from "@/components/MatchBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MediaKitPreview } from "@/components/mediakit/MediaKitPreview";
import { generateMediaKitPdf } from "@/lib/mediaKitPdf";
import { Loader2, ArrowLeft, Check, X, MessageSquare, User, MapPin, Star, UserPlus, Inbox, Send, CheckCircle2, ExternalLink, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  applied: "bg-amber-100 text-amber-700", under_review: "bg-indigo-100 text-indigo-700",
  shortlisted: "bg-purple-100 text-purple-700", accepted: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700", invited: "bg-sky-100 text-sky-700",
  waitlisted: "bg-amber-100 text-amber-700", attended: "bg-emerald-100 text-emerald-700", no_show: "bg-rose-100 text-rose-700",
};

// Optional rejection-feedback reasons (keys must match backend DECLINE_REASONS).
const DECLINE_REASONS = [
  { key: "no_reason", label: "Prefer not to give a reason" },
  { key: "audience_fit", label: "Looking for a different audience/niche fit" },
  { key: "location_fit", label: "Looking for a different location/area" },
  { key: "reach_fit", label: "Follower count or engagement not the right fit" },
  { key: "already_selected", label: "Already selected other creators for this opportunity" },
  { key: "timing", label: "Timing / not the right time" },
  { key: "other", label: "Other (use the message box)" },
];

function DeclineDialog({ open, onOpenChange, applicant, onConfirm }) {
  const [reason, setReason] = useState("no_reason");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason("no_reason"); setMessage(""); setBusy(false); } }, [open]);
  const submit = async () => {
    setBusy(true);
    try { await onConfirm(reason, message.trim()); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="decline-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Decline {applicant?.creator?.display_name || "this applicant"}?</DialogTitle>
          <DialogDescription>Sharing feedback is optional — it helps creators improve, but you can decline without giving any.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-sm font-medium text-zinc-700">Reason (optional)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="decline-reason" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{DECLINE_REASONS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-zinc-700">Message to the creator (optional)</Label>
            <Textarea data-testid="decline-message" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              maxLength={500} rows={4} placeholder="Add a short, constructive note (optional)…"
              className="mt-1.5 resize-none" />
            <p className="mt-1 text-right text-xs text-zinc-400">{message.length}/500</p>
          </div>
          <p className="rounded-xl bg-zinc-50 p-3 text-xs text-zinc-500">The creator will be told they weren't selected this time, along with any feedback you add here. This can't be changed after you confirm.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="decline-cancel" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button data-testid="decline-confirm" onClick={submit} disabled={busy} className="rounded-full bg-rose-600 hover:bg-rose-700">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <X className="mr-1 h-4 w-4" />} Confirm decline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicantCard({ a, isInvite, onAct, onAttend, opp, navigate }) {
  const [why, setWhy] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitBusy, setKitBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const isEvent = opp?.opp_type === "event";
  const eventDone = isEvent && (opp?.event_status === "completed");
  const c = a.creator || {};
  const socials = c.socials || {};
  const active = Object.keys(socials).filter((p) => socials[p] && (socials[p].username || socials[p].followers));

  const fetchKit = async () => {
    if (kit) return kit;
    setKitBusy(true);
    try { const r = await api.get(`/applications/${a.id}/media-kit`); setKit(r.data.kit); return r.data.kit; }
    catch { toast.error("Could not load this media kit"); return null; }
    finally { setKitBusy(false); }
  };
  const viewKit = async () => { const k = await fetchKit(); if (k) setKitOpen(true); };
  const downloadKit = async () => {
    setPdfBusy(true);
    try { const k = await fetchKit(); if (k) { await generateMediaKitPdf(k); toast.success("Media kit downloaded"); } }
    catch { toast.error("Could not generate the PDF"); }
    finally { setPdfBusy(false); }
  };

  return (
    <div data-testid={`applicant-${a.id}`} className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
          {c.profile_photo ? <img src={fileUrl(c.profile_photo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{c.display_name?.[0]}</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-display font-semibold text-zinc-900">{c.display_name}</p>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS[a.status]}`}>{a.status.replace("_", " ")}</span>
          </div>
          <p className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location || "—"}</span>
            {c.distance_km != null && <span className="font-semibold text-emerald-600">{c.distance_km} km</span>}
            {c.rating_count > 0 && <span className="flex items-center gap-0.5 text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {c.rating_avg}</span>}
          </p>
          <div className="mt-1 flex flex-wrap gap-1">{(c.niches || []).map((n) => <span key={n} className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">{n}</span>)}</div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <CreatorBadges badges={c.badges} />
          {a.match && <MatchBadge match={a.match} showLabel={false} testid={`applicant-match-${a.id}`} />}
        </div>
      </div>

      {a.match && (
        <div className="mt-2">
          <button data-testid={`why-match-${a.id}`} onClick={() => setWhy((w) => !w)} className="text-xs font-semibold text-emerald-700 hover:underline">
            {why ? "Hide match details" : `Why this match? · ${a.match.label}`}
          </button>
          {why && (
            <ul className="mt-2 space-y-1 rounded-xl bg-emerald-50/60 p-3">
              {a.match.reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className={r.matched ? "text-emerald-600" : "text-zinc-400"}>{r.matched ? "✓" : "○"}</span>
                  <span className={r.matched ? "text-zinc-700" : "text-zinc-500"}>{r.text}</span>
                </li>
              ))}
              <li className="pt-1 text-[11px] text-zinc-400">A recommendation to help you find relevant creators — you always decide who to work with.</li>
            </ul>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-3 text-sm">
        {active.map((p) => <span key={p} className="text-zinc-600"><span className="capitalize">{p}</span> <span className="font-mono font-semibold text-zinc-900">{formatFollowers(socials[p].followers)}</span>{socials[p].engagement ? <span className="text-emerald-600"> · {socials[p].engagement}%</span> : null}</span>)}
      </div>

      {(c.portfolio || []).length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
          {c.portfolio.slice(0, 4).map((it) => (
            <div key={it.id} className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
              {it.type === "video" ? <video src={fileUrl(it.url)} className="h-full w-full object-cover" muted /> : <img src={fileUrl(it.thumbnail || it.url)} alt="" className="h-full w-full object-cover" />}
            </div>
          ))}
        </div>
      )}

      {a.message && <p className="mt-3 rounded-xl bg-zinc-50 p-3 text-sm text-zinc-700">"{a.message}"</p>}

      {a.has_media_kit && (
        <div data-testid={`app-mediakit-${a.id}`} className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/60 p-2.5">
          <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-800"><FileText className="h-4 w-4" /> Media kit attached</span>
          <Button size="sm" variant="outline" data-testid={`app-viewkit-${a.id}`} onClick={viewKit} disabled={kitBusy} className="ml-auto rounded-full">
            {kitBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <FileText className="mr-1 h-3.5 w-3.5" />} View media kit
          </Button>
          <Button size="sm" data-testid={`app-downloadkit-${a.id}`} onClick={downloadKit} disabled={pdfBusy} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
            {pdfBusy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1 h-3.5 w-3.5" />} Download PDF
          </Button>
          <Dialog open={kitOpen} onOpenChange={setKitOpen}>
            <DialogContent data-testid={`app-kit-modal-${a.id}`} className="max-h-[85vh] max-w-2xl overflow-y-auto">
              <DialogHeader><DialogTitle>{c.display_name || "Creator"} — media kit</DialogTitle><DialogDescription>Private media kit attached to this application.</DialogDescription></DialogHeader>
              {kit ? <MediaKitPreview kit={kit} /> : <p className="text-sm text-zinc-500">No media kit to preview.</p>}
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2 border-t border-zinc-100 pt-3">
        <Button size="sm" variant="outline" data-testid={`app-viewprofile-${a.id}`} onClick={() => navigate(`/creators/${a.creator_user_id}`)} className="rounded-full"><User className="mr-1 h-3.5 w-3.5" /> Profile</Button>
        {!isInvite && ["applied", "under_review", "shortlisted", "waitlisted"].includes(a.status) && <>
          {a.status !== "shortlisted" && a.status !== "waitlisted" && <Button size="sm" variant="outline" data-testid={`app-shortlist-${a.id}`} onClick={() => onAct(a.id, "shortlisted")} className="rounded-full border-purple-200 text-purple-600 hover:bg-purple-50"><Star className="mr-1 h-3.5 w-3.5" /> Shortlist</Button>}
          <Button size="sm" data-testid={`app-accept-${a.id}`} onClick={() => onAct(a.id, "accepted")} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="mr-1 h-3.5 w-3.5" /> {isEvent ? (a.status === "waitlisted" ? "Offer space" : "Accept") : "Accept"}</Button>
          <Button size="sm" variant="outline" data-testid={`app-decline-${a.id}`} onClick={() => setDeclineOpen(true)} className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
          <DeclineDialog open={declineOpen} onOpenChange={setDeclineOpen} applicant={a}
            onConfirm={async (reason, message) => { await onAct(a.id, "declined", { decline_reason: reason, decline_message: message }); setDeclineOpen(false); }} />
        </>}
        {eventDone && a.status === "accepted" && (
          <>
            <Button size="sm" data-testid={`app-attended-${a.id}`} onClick={() => onAttend(a.id, "attended")} className={`rounded-full ${a.attendance === "attended" ? "bg-emerald-600" : "bg-zinc-200 text-zinc-700"} hover:bg-emerald-700 hover:text-white`}><Check className="mr-1 h-3.5 w-3.5" /> Attended</Button>
            <Button size="sm" variant="outline" data-testid={`app-noshow-${a.id}`} onClick={() => onAttend(a.id, "no_show")} className={`rounded-full ${a.attendance === "no_show" ? "border-rose-400 text-rose-600" : "border-zinc-200 text-zinc-500"} hover:bg-rose-50`}>No-show</Button>
          </>
        )}
        {isInvite && a.status === "invited" && <span className="rounded-full bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-600">Awaiting response</span>}
        {a.collaboration_id && <Button size="sm" variant="outline" data-testid={`app-message-${a.id}`} onClick={() => navigate(`/collaborations/${a.collaboration_id}`)} className="rounded-full"><MessageSquare className="mr-1 h-3.5 w-3.5" /> Message</Button>}
      </div>
    </div>
  );
}

export default function OpportunityManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [content, setContent] = useState([]);
  const [sortBy, setSortBy] = useState("best_match");

  const load = useCallback(() => api.get(`/opportunities/${id}/applicants`, { params: { sort: sortBy } }).then((r) => setData(r.data)).finally(() => setLoading(false)), [id, sortBy]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.opportunity?.opp_type === "event") {
      api.get(`/opportunities/${id}/summary`).then((r) => setSummary(r.data)).catch(() => {});
      api.get(`/opportunities/${id}/content`).then((r) => setContent(r.data.content || [])).catch(() => {});
    }
  }, [data, id]);
  const refreshAll = () => {
    load();
    if (data?.opportunity?.opp_type === "event") {
      api.get(`/opportunities/${id}/summary`).then((r) => setSummary(r.data)).catch(() => {});
      api.get(`/opportunities/${id}/content`).then((r) => setContent(r.data.content || [])).catch(() => {});
    }
  };

  const act = async (aid, status, extra = {}) => {
    try { await api.patch(`/applications/${aid}/status`, { status, ...extra }); toast.success(status === "declined" ? "Applicant declined" : `Applicant ${status.replace("_", " ")}`); load(); }
    catch { toast.error("Action failed"); }
  };
  const mark = async (aid, status) => {
    try { await api.patch(`/applications/${aid}/attendance`, { status }); toast.success(status === "attended" ? "Marked attended" : "Marked no-show"); refreshAll(); }
    catch { toast.error("Action failed"); }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data) return <div className="p-10 text-center text-zinc-500">Not found.</div>;
  const o = data.opportunity;
  const applications = data.applicants.filter((a) => a.type !== "invitation");
  const invitations = data.applicants.filter((a) => a.type === "invitation");

  const metrics = [
    { label: "Applicants", value: o.applicant_count, Icon: Inbox },
    { label: "Invited", value: o.invited_count, Icon: Send },
    { label: "Selected", value: o.accepted_count, Icon: CheckCircle2 },
    { label: "Places left", value: o.places_available, Icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/business/opportunities")} className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Your opportunities</button>

        <div className="rounded-3xl border border-zinc-200 bg-white p-5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-xl font-bold text-zinc-900">{o.title}</h1>
            <CompBadge type={o.compensation_type} />
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold capitalize text-zinc-600">{o.status === "published" ? "Active" : o.status}</span>
          </div>
          <p className="mt-1 text-sm text-zinc-500">{o.location} · {o.category} · {o.views} views</p>
          {o.opp_type === "event" && o.event_status !== "cancelled" && o.event_status !== "completed" && (
            <div className="mt-3"><AddToCalendar opportunity={o} testid="manage-add-calendar" label="Add event to calendar" /></div>
          )}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {metrics.map((m) => (
              <div key={m.label} data-testid={`manage-metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-xl bg-zinc-50 p-3 text-center">
                <p className="font-display text-lg font-bold text-zinc-900">{m.value}</p>
                <p className="text-[11px] text-zinc-500">{m.label}</p>
              </div>
            ))}
          </div>
          {o.status !== "closed" && (
            <Button data-testid="manage-invite-creators" variant="outline" onClick={() => navigate(`/discover?invite=${o.id}`)} className="mt-4 rounded-full">
              <UserPlus className="mr-2 h-4 w-4" /> Invite creators from Discover
            </Button>
          )}
        </div>

        {o.opp_type === "event" && summary && (
          <div data-testid="event-roi-card" className="mt-4 rounded-3xl border border-fuchsia-200 bg-white p-5">
            <h2 className="font-display text-lg font-bold text-zinc-900">Event summary</h2>
            <p className="text-sm text-zinc-500">{o.event_status === "completed" ? "Here's how your event went." : "Live tracking — final numbers after the event."}</p>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { label: "Accepted", value: summary.accepted_count },
                { label: "Attended", value: summary.attended_count },
                { label: "No-shows", value: summary.no_show_count },
                { label: "Creator reach", value: summary.reach_available ? summary.total_reach.toLocaleString() : "—" },
              ].map((m) => (
                <div key={m.label} data-testid={`roi-${m.label.replace(/\s+/g, "-").toLowerCase()}`} className="rounded-xl bg-fuchsia-50 p-3 text-center">
                  <p className="font-display text-lg font-bold text-zinc-900">{m.value}</p>
                  <p className="text-[11px] text-zinc-500">{m.label}</p>
                </div>
              ))}
            </div>
            {!summary.reach_available && (
              <p className="mt-3 text-xs text-zinc-400">Creator reach appears here once attendees submit the posts they created at your event.</p>
            )}
            {content.length > 0 && (
              <div data-testid="event-content-list" className="mt-4 border-t border-fuchsia-100 pt-3">
                <p className="mb-2 text-sm font-semibold text-zinc-900">Submitted content ({content.length})</p>
                <div className="space-y-2">
                  {content.map((c) => (
                    <div key={c.id} data-testid={`content-item-${c.id}`} className="flex items-center justify-between gap-3 rounded-xl bg-fuchsia-50/70 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-zinc-800">{c.creator_name} · <span className="capitalize">{c.platform || "post"}</span></p>
                        <a href={c.post_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 truncate text-xs text-fuchsia-700 hover:underline"><ExternalLink className="h-3 w-3" /> View post</a>
                      </div>
                      <span className="shrink-0 font-display text-sm font-bold text-zinc-900">{(c.reach || 0).toLocaleString()} <span className="text-[11px] font-normal text-zinc-500">reach</span></span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-zinc-400">Reach figures are self-reported by creators.</p>
              </div>
            )}
          </div>
        )}

        {invitations.length > 0 && (
          <>
            <h2 className="mt-6 mb-3 font-display text-lg font-bold text-zinc-900">Direct invitations</h2>
            <div className="space-y-4">
              {invitations.map((a) => <ApplicantCard key={a.id} a={a} isInvite onAct={act} onAttend={mark} opp={o} navigate={navigate} />)}
            </div>
          </>
        )}

        <div className="mt-6 mb-3 flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-zinc-900">Applicants</h2>
          {applications.length > 0 && (
            <select data-testid="applicants-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700">
              <option value="best_match">Best Match</option>
              <option value="newest">Newest</option>
              <option value="followers">Follower Count</option>
              <option value="engagement">Engagement</option>
            </select>
          )}
        </div>
        {applications.length === 0 ? (
          <div data-testid="no-applicants" className="flex h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 text-center text-sm text-zinc-500">
            No applications yet.{o.visibility !== "public" && " Invite creators to get started."}
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((a) => <ApplicantCard key={a.id} a={a} onAct={act} onAttend={mark} opp={o} navigate={navigate} />)}
          </div>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
